import Decimal from 'decimal.js-light';

import type { LimitPriceTokenSide } from './LimitPriceShortcuts';
import { formatLimitRate } from './utils';

/**
 * Stable identifier for a sell/buy token pair. The limit rate is tagged with
 * the pair key it was set for; when the rendered pair doesn't match, the rate
 * is treated as stale and the UI shows empty.
 */
export const makePairKey = (
  sell: { address: string; chainId: number } | undefined,
  buy: { address: string; chainId: number } | undefined,
): string =>
  sell && buy
    ? `${sell.chainId}:${sell.address.toLowerCase()}-${buy.chainId}:${buy.address.toLowerCase()}`
    : '';

/**
 * Compute matching sell-side and buy-side rates from a single user-entered
 * value, given which side that value represents.
 */
export const getSellAndBuyRates = (
  value: string,
  side: LimitPriceTokenSide,
): { sellRate: string; buyRate: string } => {
  if (!value) {
    return { sellRate: '', buyRate: '' };
  }

  const rateFormatted = formatLimitRate(value);
  const rateDecimal = new Decimal(rateFormatted === '.' ? 0 : rateFormatted);
  const otherRate = rateDecimal.gt(0) ? new Decimal(1).div(rateDecimal).toString() : '0';
  const otherRateFormatted = formatLimitRate(otherRate);

  return side === 'sell'
    ? { sellRate: rateFormatted, buyRate: otherRateFormatted }
    : { sellRate: otherRateFormatted, buyRate: rateFormatted };
};
